import React, { createContext, useContext, useMemo, useReducer, useRef } from 'react';

import { DEFAULT_STORY_DEFAULTS } from 'context/StoryDefaultsProvider';
import { DEFAULT_STORY_LOCALE } from 'constants/storyLocales';
import { FRAME_RENDER_HEIGHT, FRAME_RENDER_WIDTH } from 'constants/studioCanvas';

import {
  DEFAULT_WIDGET_PREVIEW,
  HISTORY_LIMIT,
  PRESET_TYPE,
} from './StudioEditorProvider.constants';
import {
  cloneElementForPaste,
  cloneFrameWithNewIds,
  commit,
  createElement,
  createFrame,
  createInitialState,
  definedOnly,
  getSelectedFrame,
  INITIAL_CONTEXT,
  mapSelectedFrameElements,
  patchElement,
  patchFrameBackdrop,
  snapshot,
} from './StudioEditorProvider.helpers';
import {
  TAction,
  TStudioContextValue,
  TStudioEditorProviderProps,
  TStudioState,
} from './StudioEditorProvider.types';

const reducer = (state: TStudioState, action: TAction): TStudioState => {
  switch (action.type) {
    case 'SET_ACTIVE_MENU':
      return { ...state, activeMenu: action.menu };

    case 'SET_PREVIEW_DEVICE':
      return { ...state, previewDevice: action.device };

    case 'SET_EDITING_LOCALE':
      return { ...state, editingLocale: action.locale };

    case 'SET_WIDGET_PREVIEW':
      return {
        ...state,
        widgetPreviews: {
          ...state.widgetPreviews,
          [action.elementId]: {
            ...(state.widgetPreviews[action.elementId] ?? DEFAULT_WIDGET_PREVIEW),
            ...action.patch,
          },
        },
      };

    case 'PLAY_ANIMATION':
      return {
        ...state,
        animationTicks: {
          ...state.animationTicks,
          [action.elementId]: (state.animationTicks[action.elementId] ?? 0) + 1,
        },
      };

    case 'SELECT_FRAME':
      return { ...state, selectedFrameId: action.frameId, selectedElementId: null };

    case 'SELECT_ELEMENT':
      return { ...state, selectedElementId: action.elementId };

    case 'ADD_ELEMENT': {
      const frame = getSelectedFrame(state);
      if (!frame) {
        return state;
      }
      const created = createElement(action.preset, frame.elements.length + 1, action.storyDefaults);
      const base = action.patch ? { ...created, ...action.patch } : created;
      const sameKindCount = frame.elements.filter(
        (existing) => existing.type === PRESET_TYPE[action.preset]
      ).length;
      const offset = (sameKindCount % 5) * 4;
      let element = base;
      if (offset) {
        element = base.fitWidth
          ? { ...base, y: base.y + offset }
          : { ...base, x: base.x + offset, y: base.y + offset };
      }
      const frames = mapSelectedFrameElements(state, (elements) => [...elements, element]);
      return commit(state, { frames, selectedElementId: element.id });
    }

    case 'UPDATE_ELEMENT': {
      const frames = patchElement(state, action.elementId, (element) => ({
        ...element,
        ...action.patch,
      }));
      return commit(state, { frames });
    }

    case 'UPDATE_ELEMENT_STYLE': {
      const frames = patchElement(state, action.elementId, (element) => ({
        ...element,
        style: { ...element.style, ...action.patch },
      }));
      return commit(state, { frames });
    }

    case 'MOVE_ELEMENT': {
      const frames = patchElement(state, action.elementId, (element) => ({
        ...element,
        x: action.x,
        y: action.y,
      }));
      return commit(state, { frames });
    }

    // An emoji's size is its font size, so a corner drag has to rewrite it — otherwise the box
    // and the glyph drift apart. `keepRatio` keeps the box square in px, so the `min` is exact.
    case 'RESIZE_ELEMENT': {
      const frames = patchElement(state, action.elementId, (element) =>
        element.type === 'emoji'
          ? {
              ...element,
              ...action.box,
              style: {
                ...element.style,
                fontSize: Math.min(
                  (action.box.width * FRAME_RENDER_WIDTH) / 100,
                  (action.box.height * FRAME_RENDER_HEIGHT) / 100
                ),
              },
            }
          : { ...element, ...action.box }
      );
      return commit(state, { frames });
    }

    case 'ROTATE_ELEMENT': {
      const frames = patchElement(state, action.elementId, (element) => ({
        ...element,
        rotation: action.rotation,
      }));
      return commit(state, { frames });
    }

    case 'DELETE_ELEMENT': {
      const frames = mapSelectedFrameElements(state, (elements) =>
        elements.filter((element) => element.id !== action.elementId)
      );
      const selectedElementId =
        state.selectedElementId === action.elementId ? null : state.selectedElementId;
      return commit(state, { frames, selectedElementId });
    }

    case 'DUPLICATE_ELEMENT': {
      const frame = getSelectedFrame(state);
      const source = frame?.elements.find((element) => element.id === action.elementId);
      if (!frame || !source) {
        return state;
      }
      const clone = cloneElementForPaste(source, frame.elements.length + 1);
      const frames = mapSelectedFrameElements(state, (elements) => [...elements, clone]);
      return commit(state, { frames, selectedElementId: clone.id });
    }

    case 'COPY_ELEMENT': {
      const frame = getSelectedFrame(state);
      const source = frame?.elements.find((element) => element.id === action.elementId);
      if (!source) {
        return state;
      }
      return { ...state, clipboard: structuredClone(source) };
    }

    case 'CUT_ELEMENT': {
      const frame = getSelectedFrame(state);
      const source = frame?.elements.find((element) => element.id === action.elementId);
      if (!source) {
        return state;
      }
      const frames = mapSelectedFrameElements(state, (elements) =>
        elements.filter((element) => element.id !== action.elementId)
      );
      const selectedElementId =
        state.selectedElementId === action.elementId ? null : state.selectedElementId;
      return {
        ...commit(state, { frames, selectedElementId }),
        clipboard: structuredClone(source),
      };
    }

    case 'PASTE_ELEMENT': {
      const frame = getSelectedFrame(state);
      if (!frame || !state.clipboard) {
        return state;
      }
      const clone = cloneElementForPaste(state.clipboard, frame.elements.length + 1);
      const frames = mapSelectedFrameElements(state, (elements) => [...elements, clone]);
      return commit(state, { frames, selectedElementId: clone.id });
    }

    case 'ADD_FRAME': {
      const frame = createFrame(action.storyDefaults);
      return commit(state, {
        frames: [...state.frames, frame],
        selectedFrameId: frame.id,
        selectedElementId: null,
      });
    }

    case 'DUPLICATE_FRAME': {
      const index = state.frames.findIndex((frame) => frame.id === action.frameId);
      if (index === -1) {
        return state;
      }
      const clone = cloneFrameWithNewIds(state.frames[index]);
      const frames = [...state.frames.slice(0, index + 1), clone, ...state.frames.slice(index + 1)];
      return commit(state, { frames, selectedFrameId: clone.id, selectedElementId: null });
    }

    case 'DELETE_FRAME': {
      if (state.frames.length <= 1) {
        return state;
      }
      const index = state.frames.findIndex((frame) => frame.id === action.frameId);
      const frames = state.frames.filter((frame) => frame.id !== action.frameId);
      const selectedFrameId =
        state.selectedFrameId === action.frameId
          ? frames[Math.max(0, index - 1)].id
          : state.selectedFrameId;
      return commit(state, { frames, selectedFrameId, selectedElementId: null });
    }

    case 'MOVE_FRAME': {
      const index = state.frames.findIndex((frame) => frame.id === action.frameId);
      const target = action.direction === 'back' ? index - 1 : index + 1;
      if (index === -1 || target < 0 || target >= state.frames.length) {
        return state;
      }
      const frames = [...state.frames];
      [frames[index], frames[target]] = [frames[target], frames[index]];
      return commit(state, { frames });
    }

    /*
     * The four backdrop cases below go through `patchFrameBackdrop`, which decides whether the
     * change lands on the frame or on the editing locale's override. Nothing here knows about
     * locales — that is the point.
     */

    case 'SET_FRAME_BACKGROUND': {
      const frames = patchFrameBackdrop(state, action.frameId, (backdrop) => ({
        ...backdrop,
        background: action.color,
      }));
      return commit(state, { frames });
    }

    case 'SET_FRAME_COVER': {
      const frames = patchFrameBackdrop(state, action.frameId, (backdrop) => ({
        ...backdrop,
        cover: {
          type: action.coverType ?? 'image',
          mediaUrl: action.mediaUrl,
          posterUrl: action.posterUrl,
          fit: backdrop.cover?.fit ?? 'fit',
          position: backdrop.cover?.position ?? 'center',
          fillColor: action.fillColor ?? backdrop.cover?.fillColor ?? '#000000',
        },
      }));
      return commit(state, { frames });
    }

    /**
     * The panel edits cover keys and frame-level settings through one control group, so the patch
     * carries all three. Split rather than spread: `name`/`statusBar` live on the frame and are
     * shared across languages, `durationSeconds` lands on the editing locale, and everything else
     * merges into the backdrop's `cover`. Undefined keys are dropped so a single-key patch cannot
     * blank its siblings.
     */
    case 'UPDATE_FRAME_COVER': {
      const { name, statusBar, durationSeconds, ...coverPatch } = action.patch;
      const framePatch = definedOnly({ name, statusBar });
      const cover = definedOnly(coverPatch);
      const locale = state.editingLocale;

      // Only the cover half and the duration are localized, so the shared half is applied first —
      // which also keeps a name-only patch from seeding an empty override.
      const shared = state.frames.map((frame) => {
        if (frame.id !== action.frameId) {
          return frame;
        }
        const patched = { ...frame, ...framePatch };
        if (durationSeconds === undefined) {
          return patched;
        }
        // English is the fallback every other locale reads, so it writes the shared field itself.
        return locale === DEFAULT_STORY_LOCALE
          ? { ...patched, durationSeconds }
          : { ...patched, durations: { ...patched.durations, [locale]: durationSeconds } };
      });
      if (Object.keys(cover).length === 0) {
        return commit(state, { frames: shared });
      }
      const frames = patchFrameBackdrop(
        { ...state, frames: shared },
        action.frameId,
        (backdrop) => ({
          ...backdrop,
          cover: { ...backdrop.cover, ...cover },
        })
      );
      return commit(state, { frames });
    }

    case 'REMOVE_FRAME_COVER': {
      const frames = patchFrameBackdrop(state, action.frameId, (backdrop) => ({
        ...backdrop,
        cover: undefined,
      }));
      return commit(state, { frames });
    }

    /**
     * Drops the editing locale's override entirely, so the slide falls back to English again.
     * A no-op under English, which has no override to drop — it IS the fallback.
     */
    case 'RESET_FRAME_LOCALE': {
      if (state.editingLocale === DEFAULT_STORY_LOCALE) {
        return state;
      }
      const frames = state.frames.map((frame) => {
        if (frame.id !== action.frameId || !frame.translations?.[state.editingLocale]) {
          return frame;
        }
        const translations = { ...frame.translations };
        delete translations[state.editingLocale];
        return { ...frame, translations };
      });
      return commit(state, { frames });
    }

    /**
     * Drops the editing locale's duration override, so the slide falls back to the shared value.
     * Separate from `RESET_FRAME_LOCALE` because the backdrop and the duration are overridden
     * independently — resetting one must not silently discard the other.
     */
    case 'RESET_FRAME_DURATION_LOCALE': {
      if (state.editingLocale === DEFAULT_STORY_LOCALE) {
        return state;
      }
      const frames = state.frames.map((frame) => {
        if (frame.id !== action.frameId || frame.durations?.[state.editingLocale] === undefined) {
          return frame;
        }
        const durations = { ...frame.durations };
        delete durations[state.editingLocale];
        return { ...frame, durations };
      });
      return commit(state, { frames });
    }

    case 'UNDO': {
      if (state.past.length === 0) {
        return state;
      }
      const previous = state.past[state.past.length - 1];
      return {
        ...state,
        ...previous,
        past: state.past.slice(0, -1),
        future: [snapshot(state), ...state.future].slice(0, HISTORY_LIMIT),
      };
    }

    case 'REDO': {
      if (state.future.length === 0) {
        return state;
      }
      const next = state.future[0];
      return {
        ...state,
        ...next,
        past: [...state.past, snapshot(state)].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
      };
    }

    default:
      return state;
  }
};

const StudioEditorContext = createContext<TStudioContextValue>(INITIAL_CONTEXT);

export const useStudioEditorContext = (): TStudioContextValue => useContext(StudioEditorContext);

const StudioEditorProvider = ({
  children,
  initialFrames,
  attributeKeys = [],
  sampleMembers = [],
  storyDefaults = DEFAULT_STORY_DEFAULTS,
}: TStudioEditorProviderProps) => {
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    createInitialState(initialFrames, storyDefaults)
  );

  const baselineRef = useRef<string>(JSON.stringify(state.frames));

  const value = useMemo<TStudioContextValue>(() => {
    const selectedFrame = state.frames.find((frame) => frame.id === state.selectedFrameId);
    const selectedElement =
      selectedFrame?.elements.find((element) => element.id === state.selectedElementId) ?? null;

    return {
      ...state,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      canPaste: state.clipboard !== null,
      isDirty: JSON.stringify(state.frames) !== baselineRef.current,
      selectedElement,
      selectedFrame: selectedFrame ?? null,
      attributeKeys,
      sampleMembers,
      getWidgetPreview: (elementId) => state.widgetPreviews[elementId] ?? DEFAULT_WIDGET_PREVIEW,
      getAnimationTick: (elementId) => state.animationTicks[elementId] ?? 0,
      setActiveMenu: (menu) => dispatch({ type: 'SET_ACTIVE_MENU', menu }),
      setPreviewDevice: (device) => dispatch({ type: 'SET_PREVIEW_DEVICE', device }),
      setEditingLocale: (locale) => dispatch({ type: 'SET_EDITING_LOCALE', locale }),
      setWidgetPreview: (elementId, patch) =>
        dispatch({ type: 'SET_WIDGET_PREVIEW', elementId, patch }),
      playAnimation: (elementId) => dispatch({ type: 'PLAY_ANIMATION', elementId }),
      selectFrame: (frameId) => dispatch({ type: 'SELECT_FRAME', frameId }),
      selectElement: (elementId) => dispatch({ type: 'SELECT_ELEMENT', elementId }),
      addElement: (preset, patch) =>
        dispatch({ type: 'ADD_ELEMENT', preset, patch, storyDefaults }),
      updateElement: (elementId, patch) => dispatch({ type: 'UPDATE_ELEMENT', elementId, patch }),
      updateElementStyle: (elementId, patch) =>
        dispatch({ type: 'UPDATE_ELEMENT_STYLE', elementId, patch }),
      moveElement: (elementId, x, y) => dispatch({ type: 'MOVE_ELEMENT', elementId, x, y }),
      resizeElement: (elementId, box) => dispatch({ type: 'RESIZE_ELEMENT', elementId, box }),
      rotateElement: (elementId, rotation) =>
        dispatch({ type: 'ROTATE_ELEMENT', elementId, rotation }),
      deleteElement: (elementId) => dispatch({ type: 'DELETE_ELEMENT', elementId }),
      duplicateElement: (elementId) => dispatch({ type: 'DUPLICATE_ELEMENT', elementId }),
      copyElement: (elementId) => dispatch({ type: 'COPY_ELEMENT', elementId }),
      cutElement: (elementId) => dispatch({ type: 'CUT_ELEMENT', elementId }),
      pasteElement: () => dispatch({ type: 'PASTE_ELEMENT' }),
      addFrame: () => dispatch({ type: 'ADD_FRAME', storyDefaults }),
      duplicateFrame: (frameId) => dispatch({ type: 'DUPLICATE_FRAME', frameId }),
      deleteFrame: (frameId) => dispatch({ type: 'DELETE_FRAME', frameId }),
      moveFrame: (frameId, direction) => dispatch({ type: 'MOVE_FRAME', frameId, direction }),
      setFrameBackground: (frameId, color) =>
        dispatch({ type: 'SET_FRAME_BACKGROUND', frameId, color }),
      setFrameCover: (frameId, mediaUrl, fillColor, coverType, posterUrl) =>
        dispatch({ type: 'SET_FRAME_COVER', frameId, mediaUrl, fillColor, coverType, posterUrl }),
      updateFrameCover: (frameId, patch) =>
        dispatch({ type: 'UPDATE_FRAME_COVER', frameId, patch }),
      removeFrameCover: (frameId) => dispatch({ type: 'REMOVE_FRAME_COVER', frameId }),
      resetFrameLocale: (frameId) => dispatch({ type: 'RESET_FRAME_LOCALE', frameId }),
      resetFrameDuration: (frameId) => dispatch({ type: 'RESET_FRAME_DURATION_LOCALE', frameId }),
      undo: () => dispatch({ type: 'UNDO' }),
      redo: () => dispatch({ type: 'REDO' }),
    };
  }, [state, attributeKeys, sampleMembers, storyDefaults]);

  return <StudioEditorContext.Provider value={value}>{children}</StudioEditorContext.Provider>;
};

export default StudioEditorProvider;
